import {
    CountrySpecificConfig,
    DocumentType,
    LaunchDarklyProject,
    PushNotificationType,
} from '@superbet-group/web.lib.config';

import BonusProvider from '@core/models/bonus/BonusProvider';
import { Environment } from '@src/config/enums';
import { appLocale } from '@src/config/locale';
import { serviceProps } from '@src/config/playerAccountManagement/Comtrade';
import { HistoryType } from '@store/modules/ui/user/history/enums';
import { getIdApiUrls } from '@accountFederation/modules/idCardVerification/getid/constants';
import { isMeticulousContext } from '@src/app/utils/meticulous/meticulousUtils';
import { Country } from '@superbet-group/web.types';

const defaultProjectLDClientIds: Record<Environment, string> = {
    Dev: '60f552393a13fa259adaacc5',
    Qa: '60f55295803ad725c4998b67',
    Stage: '60f552d49707b8256057b32e',
    Prod: '60f56646b046d624df8c3ad9',
};

const serviceStatusLDClientIds: Record<Environment, string> = {
    Dev: '614c510b0c50ac266b459f6e',
    Qa: '614c510b0c50ac266b459f6e',
    Stage: '614c510b0c50ac266b459f6e',
    Prod: '614c50b096336326bb7d24ed',
};

const surveysLDClientIds: Record<Environment, string> = {
    Dev: '626fbc205a57c7150b062168',
    Qa: '626fbc9b3e7eb414a6ee73e8',
    Stage: '626fbc36ae799e15c1e137b7',
    Prod: '626fbd0997a47e14e4b8b908',
};

const config: CountrySpecificConfig = {
    country: Country.Ro,
    waitForRouteConfig: false,
    isBingoEnabled: true,
    checkSessionBeforeGameLaunch: false,
    services: {
        cashout: {
            isAvailable: true,
        },
    },
    header: {
        navbarItems: [
            'promotionHub',
            'sport',
            'live',
            'games',
            'casinoLive',
            'bingo',
            'virtual',
            'lottery',
            'social',
            'results',
            'news',
            'promotions',
        ],
        items: [
            'sport',
            'live',
            'games',
            'casinoLive',
            'bingo',
            'virtual',
            'lottery',
            'news',
            'results',
            'shops',
        ],
    },
    auth: {
        usernameValidChars: '[A-Za-z0-9@$!%*#?&ăâîşţąćęłńóśźż]+',
        nameValidChars: "[A-Za-zăĂâÂîÎșȘțȚȘșȚțąćęłńóśźżÀ-ÖØ-öø-ÿ0-9\\-'\\s]+",
        portalDesktopId: 'Desktop_new',
        portalMobileId: 'Mobile_new',
        registerUrl: 'register',
        countryId: 184,
        emailMaxLength: 50,
        username: {
            requirements: {
                minLength: 6,
                maxLength: 20,
            },
        },
        password: {
            requirements: {
                minLength: 8,
                maxLength: 30,
                hasLowercase: true,
                hasUppercase: true,
                hasDigit: true,
            },
            isDistinctFromUsername: true,
            isDistinctFromEmail: true,
        },
    },
    user: {
        activateAccountRestRoute: 'activateWithLogin',
        idCardVerification: {
            period: 30, // in days. if user doesn't upload id card account will be disabled,
            isCompleteRegistrationNeededForKyc: false,
            maxRequiredDocumentImages: 1,
            isRemainingTimeVisible: true,
        },
        withdrawal: {
            detailsPaysafecardWithdrawalUrl: 'https://suport.superbet.ro/hc/ro/articles/360000261229-Retragere-prin-Paysafecard-',
            detailsOnlineWithdrawalUrl: 'https://suport.superbet.ro/hc/ro/articles/360000302765-Care-sunt-metodele-de-retragere-',
            detailsBankTransferWithdrawalUrl: 'https://suport.superbet.ro/hc/ro/articles/360000302685-Retragere-prin-Transfer-bancar',
            detailsShopWithdrawalUrl: 'https://suport.superbet.ro/hc/ro/articles/360000312629-Retragere-prin-intermediul-Agen%C8%9Biilor-',
            detailsInstantWithdrawalUrl: 'https://suport.superbet.ro/hc/ro/articles/360000302705-Retragere-instant',
            isTaxable: true,
            isNewLabelVisible: {},
            hasFees: true,
            isDepositRequired: true,
            idCardVerificationCheck: {
                isRequired: true,
                isRemainingTimeVisible: true,
            },
            walletBreakdown: {
                isEnabled: false,
                isShown: false,
            },
        },
        deposit: {
            detailsOnlineDepositUrl: 'https://suport.superbet.ro/hc/ro/articles/360000308129-Care-sunt-metodele-de-depunere-online-',
            detailsShopDepositUrl: 'https://suport.superbet.ro/hc/ro/articles/360000308149-Depunere-Cash-prin-intermediul-Agen%C8%9Biilor-',
            detailsPaysafecardDepositUrl: 'https://suport.superbet.ro/hc/ro/articles/360000308249-Depunere-prin-Paysafecard',
            detailsBankTransferUrl: 'https://suport.superbet.ro/hc/ro/articles/360000308169-Depunere-prin-Transfer-bancar',
            hasEligibility: false,
            idCardVerificationCheck: {
                isRequired: false,
                isRemainingTimeVisible: false,
            },
            isTaxable: true,
            isNewLabelVisible: {},
            bankTransfer: {
                iban: 'RO56 RZBR 0000 0600 2595 9883',
                bank: 'Raiffeisen Romania',
                currency: 'RON',
                accountName: 'MJP Interactive LTD',
                swift: 'RZBRROBU',
                city: 'București',
            },
        },
        history: {
            availableTypes: {
                [HistoryType.transactions]: true,
                [HistoryType.bets]: true,
                [HistoryType.casinoPlays]: true,
            },
        },
        bonus: {
            faqUrlActive: 'https://suport.superbet.ro/hc/ro/articles/360016566839-Cum-v%C4%83d-bonusurile-active-din-contul-meu-',
            faqUrlPending: 'https://suport.superbet.ro/hc/ro/articles/360016534960-Cum-v%C4%83d-ce-bonusuri-am-%C3%AEn-a%C8%99teptare-%C8%99i-cum-pot-intra-%C3%AEn-posesia-lor-',
            isWelcomeBonusShown: true,
            bonusProvider: BonusProvider.Comtrade,
            isLiveDealerBonusVisible: false,
            isWelcomeBonusIntroModalVisible: true,
            availableDetailFields: {
                bonusAmount: true,
                redeemedAmount: true,
                wageringRequirements: true,
                wageredAmount: true,
                amountLeftToWager: false,
                spinCasinoDetail: false,
                acceptanceDate: true,
                activationDate: true,
                termsAndConditionsUrl: true,
                expirationDate: false,
                amountGained: false,
            },
        },
        settings: {
            phoneDigitsNumber: 10,
            isPhoneWithPrefixEnabled: false,
            defaultCountryPrefix: '+40',
        },
        responsibleGaming: {
            depositLimitUrl: 'https://suport.superbet.ro/hc/ro/articles/360001645779-Limit%C4%83-la-depunere',
            realityCheckUrl: '',
            betLimitUrl: 'https://suport.superbet.ro/hc/ro/articles/360001292660-Limite-de-pariere',
            selfExclusionInfoUrl: 'https://suport.superbet.ro/hc/ro/articles/360001464425-Autoexcludere',
            responsibleGamingInfoUrl: 'https://suport.superbet.ro/hc/ro/articles/360001456169-Joc-responsabil',
            accountClosureInfoUrl: 'https://suport.superbet.ro/hc/ro/articles/360000308509-%C3%8Enchidere-cont',
            isSelfExclusionModalPromotionsTextVisible: true,
            timeOut: {
                periods: [
                    {
                        name: '1day',
                        label: '1 day',
                        type: 'days',
                        value: 1,
                    },
                    {
                        name: '2days',
                        label: '2 days',
                        type: 'days',
                        value: 2,
                    },
                    {
                        name: '3days',
                        label: '3 days',
                        type: 'days',
                        value: 3,
                    },
                    {
                        name: '4days',
                        label: '4 days',
                        type: 'days',
                        value: 4,
                    },
                    {
                        name: '5days',
                        label: '5 days',
                        type: 'days',
                        value: 5,
                    },
                    {
                        name: '6days',
                        label: '6 days',
                        type: 'days',
                        value: 6,
                    },
                    {
                        name: '7days',
                        label: '7 days',
                        type: 'days',
                        value: 7,
                    },
                    {
                        name: '2weeks',
                        label: '2 weeks',
                        type: 'days',
                        value: 14,
                    },
                    {
                        name: '1month',
                        label: '1 month',
                        type: 'months',
                        value: 1,
                    },
                    {
                        name: '3months',
                        label: '3 months',
                        type: 'months',
                        value: 3,
                    },
                ],
                customMaxMonths: 6,
            },
            selfExclusionDefaultPeriod: 6,
        },
        isUpdateCustomParamsEnabled: true,
        isLicencePreferencesEnabled: false,
    },
    multiStepIdVerification: {
        documentSettings: {
            [DocumentType.Id]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
            [DocumentType.Passport]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
            [DocumentType.AdditionalDocument]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
        },
    },
    sport: {
        isStreamAvailable: true,
        excludedStreamTournaments: [],
        v5Config: {
            language: appLocale.languageCode,
            applicationVariant: 'rosuperbetsport',
        },
        timeMarginForEventStart: 3,
    },
    utils: {
        bookieShopStartTime: 10,
        dateOfBirthFormat: 'yyyy-MM-dd',
    },
    footer: {
        whatsAppNumber: '',
        nonStopPhoneNumber: '',
        email: 'suport@superbet.ro',
        faqPage: 'https://suport.superbet.ro/hc/ro',
        offers: {
            daily: '/pdf/Superbet - Oferta zilnică.pdf?var=',
            weekly: '/pdf/Superbet - Oferta săptămânală.pdf?var=',
        },
        social: {
            twitter: 'https://twitter.com/SuperbetRomania',
            facebook: 'https://www.facebook.com/superbet.ro/',
            youtube: 'https://www.youtube.com/channel/UCL6Qc_XWLUHBq1NYwu7prCA',
            instagram: 'https://www.instagram.com/superbetromania/',
            tiktok: '',
        },
    },
    defaultLocation: {
        latitude: 44.439663,
        longitude: 26.096306,
    },
    getSessionDuration: 2 * 60 * 1000, // 2 minutes
    isCasinoEnabled: true,
    showXJackpotOnPromoPage: true,
    news: {
        pageSize: 50,
        host: 'https://superbet-sportal-production.freetls.fastly.net/',
        project: 'superbet.ro',
    },
    corona: {
        areShopsDisabled: false,
    },
    supportEmail: 'suport@superbet.ro',
    virtualTicketsTranslations: {
        over: 'peste',
        under: 'sub',
    },
    betSlip: {
        allowSubmitWithMaxWin: true,
        showPayoutShoppingList: true,
        validationsConfig: {
            isEnabledWinLowerThanStake: true,
        },
    },
    marketFilters: {
        isEnabled: true,
    },
    maintenance: {
        defaultMaintenanceUrl: 'https://www.superbet.today/03/',
    },
    multiStepTaxForSystemTickets: false,
    isPrintHistoryEnabled: false,
    launchDarkly: {
        clientIds: {
            // Stage is the default so that we can run Meticulous correctly
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Stage,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Stage,
            [LaunchDarklyProject.Surveys]: surveysLDClientIds.Stage,
        },
        userKeyVariable: serviceProps.launchDarklyUserKeyVariable,
    },
    social: {
        country: Country.Ro,
        env: 'stage',
    },
    pushNotificationType: PushNotificationType.NotificationSocketService,
    useSessionActivity: false,
    moreInformationLink: 'https://suport.superbet.ro/hc/ro/articles/360012658159-Agen%C8%9Bii-%C3%AEnchise-',
    isSessionDurationDisplayed: true,
    inbox: {
        availableDeepLinks: ['rosuperbetsport', 'rosuperbetgames'],
    },
};

switch (window.location.hostname) {
    case 'www.superbet.ro':
    case 'superbet.ro':
    case 'local.prod.superbet.ro':
    case 'www.silent.superbet.ro':
    case 'silent.superbet.ro':
    case 'stage.on.superbet.ro':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Prod,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Prod,
            [LaunchDarklyProject.Surveys]: surveysLDClientIds.Prod,
        };
        config.getIdApiUrl = getIdApiUrls.Prod;
        config.social.env = 'prod';
        break;
    case 'stage.superbet.ro':
    case 'playground.superbet.ro':
    case 'www.stage.superbet.ro':
    case 'local.stage.superbet.ro':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Stage,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Stage,
            [LaunchDarklyProject.Surveys]: surveysLDClientIds.Stage,
        };
        config.getIdApiUrl = getIdApiUrls.Stage;
        break;
    case 'qa.superbet.ro':
    case 'www.qa.superbet.ro':
    case 'local.qa.superbet.ro':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Qa,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Qa,
            [LaunchDarklyProject.Surveys]: surveysLDClientIds.Qa,
        };
        config.getIdApiUrl = getIdApiUrls.Qa;
        break;
    case 'dev.superbet.ro':
    case 'www.dev.superbet.ro':
    case 'local.dev.superbet.ro':
    case 'local.superbet.ro':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Dev,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Dev,
            [LaunchDarklyProject.Surveys]: surveysLDClientIds.Dev,
        };
        config.getIdApiUrl = getIdApiUrls.Dev;
        break;
    default:
        if (isMeticulousContext()) {
            config.getIdApiUrl = getIdApiUrls.Stage; // Mocked for meticulous runs
        }
        break;
}

export default config;
